import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useMobxStores } from '@portal/env/hooks/useMobxStores';
import { BasketBookingData } from '@portal/types/nexudus';
import { refreshFullCalendar } from '@bw-ui/lib/refreshFullCalendar';
import { parseNexudusErrorMessage } from '@portal/env/utils/nexudusErrors';

interface CancelBookingSnapshot {
  id: number;
  resourceName: string;
  fromTime: string;
  toTime: string;
  resourceImageUrl: string;
}

/**
 * Manages the cancel-booking dialog flow:
 * 1. Captures a snapshot of the booking and loads cancellation fee details just incase booking state changes
 * 2. Opens the confirmation dialog
 * 3. Delegates the actual deletion to a `useMutation` (handles pending/error state)
 * 4. On success, shows a "confirmed" state before closing
 *
 * The dialog state (open, complete, snapshot) lives alongside the mutation
 * rather than replacing it — the mutation owns the async delete lifecycle.
 */
export const useCancelBooking = (props: {
  close: () => void;
  showBookingModal: () => void;
  hideBookingModal: () => void;
}) => {
  const { appStore, bookingsStore } = useMobxStores();

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoadingFee, setIsLoadingFee] = useState(false);
  const [cancellationFee, setCancellationFee] = useState<number | null>(null);
  const [snapshot, setSnapshot] = useState<CancelBookingSnapshot | null>(null);

  const currencyCode =
    (appStore.business as unknown as { Currency?: { Code?: string } })?.Currency
      ?.Code || 'GBP';
  const resourceImageBaseUrl =
    (appStore.business as unknown as { NativeHomeUrlWithLanguage?: string })
      ?.NativeHomeUrlWithLanguage ?? '';

  // Mutation wraps the full cancel flow so isPending stays true
  // from the moment the user confirms until the success state renders
  const deleteBookingMutation = useMutation({
    mutationKey: ['deleteBooking'],
    mutationFn: async (bookingId: number) => {
      const result = await bookingsStore.deleteBooking(bookingId);

      if (!result?.WasSuccessful) {
        setShowCancelDialog(false);
        await appStore.setPopMessage(
          result?.Message
            ? result.Message
            : 'Please contact this space to change this booking.',
        );
        props.showBookingModal();
        return;
      }

      try {
        await bookingsStore.loadUpcomingBookings();
        refreshFullCalendar();
      } catch {
        // Best-effort refresh — the booking is already deleted
      }
      setIsComplete(true);
    },
    onError: (error) => {
      setShowCancelDialog(false);
      const rawMessage = (
        error as { response?: { body?: { Message?: unknown } } }
      )?.response?.body?.Message;
      const parsedMessage =
        typeof rawMessage === 'string'
          ? parseNexudusErrorMessage(rawMessage)
          : null;
      appStore
        .setPopMessage(
          parsedMessage ??
            'Could not cancel this booking. Please try again later.',
        )
        .catch(() => {
          /* Do nothing */
        });
      props.showBookingModal();
    },
  });

  const openCancelDialog = async (manualBooking?: BasketBookingData) => {
    const booking =
      manualBooking || (bookingsStore.booking as null | BasketBookingData);
    if (!booking) return;

    const captured: CancelBookingSnapshot = {
      id: booking.Id!,
      resourceName: booking.ResourceName,
      fromTime: booking.FromTime,
      toTime: booking.ToTime,
      resourceImageUrl: `${resourceImageBaseUrl}/publicresources/getimage/${booking.ResourceId}?h=128&w=128`,
    };
    setSnapshot(captured);

    bookingsStore.resetBookingChangeTracking();
    props.hideBookingModal();

    setIsComplete(false);
    setCancellationFee(null);
    setIsLoadingFee(true);
    setShowCancelDialog(true);

    try {
      await bookingsStore.loadBookingCancellationFeeDetails(captured.id);
      const { hasCancellationFee, cancellationFee: fee } =
        bookingsStore.cancellationDetails;
      setCancellationFee(hasCancellationFee ? fee : null);
    } catch {
      setCancellationFee(null);
    } finally {
      setIsLoadingFee(false);
    }
  };

  const confirmCancel = () => {
    if (!snapshot) return;
    deleteBookingMutation.mutate(snapshot.id);
  };

  const keepBooking = () => {
    setShowCancelDialog(false);
    props.showBookingModal();
  };

  const closeAfterCancel = () => {
    setShowCancelDialog(false);
    props.close();
  };

  return {
    showCancelDialog,
    isComplete,
    isLoadingFee,
    isDeleting: deleteBookingMutation.isPending,
    cancellationFee,
    currencyCode,
    snapshot,
    openCancelDialog,
    confirmCancel,
    keepBooking,
    closeAfterCancel,
  };
};
